import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Building2,
  ChevronDown,
  Maximize2,
  MessageSquare,
  PanelRightClose,
  Sparkles,
  SquarePen,
  User,
  Users,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { shortcutLabel } from "@/lib/keyboard";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useProfile } from "@/context/ProfileContext";
import { useTeamScope } from "@/context/TeamScopeContext";
import { useCoachRail } from "@/context/CoachRailContext";
import { useIntelligenceAccess } from "@/hooks/use-intelligence-access";
import { useCanDockInspector } from "@/hooks/use-inspector-dock";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIntelligenceTopics } from "@/hooks/queries/use-intelligence-topics";
import {
  useSalesTeams,
  useSalesTeamsMembers,
} from "@/hooks/queries/use-sales-teams";
import { useSalesInsightThread } from "@/hooks/queries/use-sales-insight-threads";
import { useIntelligenceScope } from "@/hooks/queries/use-intelligence-scope";
import { IntelligenceChat } from "@/components/intelligence/IntelligenceChat";
import { ScopePicker } from "@/components/intelligence/ScopePicker";
import {
  parseScopeParam,
  scopeToParam,
  type ChatScope,
} from "@/components/intelligence/scope";
import {
  deriveChatScope,
  deriveNonOrgDefaultScope,
  deriveTopicKey,
} from "@/components/intelligence/chat-scope-derivation";
import { getLocalizedEffectiveTopics } from "@/components/intelligence/topics";
import { CoachThreadPicker } from "./CoachThreadPicker";
import { useRailStarters } from "./rail-starters";

/**
 * The rail is suppressed only on the full-page chat, which owns the
 * conversation there; every other Coach page (`/coach/teams`, …) keeps it.
 */
function isRailSuppressed(pathname: string): boolean {
  return pathname === "/coach";
}

/**
 * The docked coach rail (4a/4b): `IntelligenceChat` lifted into a persistent
 * layout slot so its thread survives navigating Today → Candidates → Playbook.
 * Reads cross-route state from {@link useCoachRail}. Renders as a flush 452px
 * column on wide viewports (collapsing to a 44px edge strip when closed) and as
 * a right overlay sheet below the dock breakpoint.
 */
export function CoachRail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("intelligence");
  const { businessProfile } = useProfile();
  const businessId = businessProfile?.uid ?? "";
  const entitled = useIntelligenceAccess();
  const canDock = useCanDockInspector();
  const isMobile = useIsMobile();

  const {
    open,
    openRail,
    closeRail,
    activeThreadId,
    selectThread,
    newThread,
    resetNonce,
    threadPickerOpen,
    setThreadPickerOpen,
    scopeParam,
    setScopeParam,
    topicParam,
    setTopicParam,
    pendingMessage,
    clearPendingMessage,
  } = useCoachRail();

  const suppressed = isRailSuppressed(location.pathname);

  // ── Data ──────────────────────────────────────────────────────────────────
  const topicsQuery = useIntelligenceTopics(businessId);
  const topics = getLocalizedEffectiveTopics(t, topicsQuery.data?.topics);

  const teamsQuery = useSalesTeams(businessId);
  const membersQuery = useSalesTeamsMembers(businessId);
  const scopeAccessQuery = useIntelligenceScope(businessId);
  const restrictToSubtree = scopeAccessQuery.data?.mode === "scoped";
  const { activeTeamId } = useTeamScope();

  const threadQuery = useSalesInsightThread(businessId, activeThreadId);

  const membersById = useMemo(() => {
    const m = new Map<string, { salesTeamId: string | null }>();
    for (const mem of membersQuery.data ?? [])
      m.set(mem.id, { salesTeamId: mem.salesTeamId });
    return m;
  }, [membersQuery.data]);

  const activeTeamScope = useMemo((): ChatScope | null => {
    if (!activeTeamId) return null;
    const teams = teamsQuery.data ?? [];
    return teams.some((tm) => tm.id === activeTeamId)
      ? { kind: "team", salesTeamId: activeTeamId }
      : null;
  }, [activeTeamId, teamsQuery.data]);

  const parsedScope = useMemo(
    () => parseScopeParam(scopeParam, membersById),
    [scopeParam, membersById],
  );

  const nonOrgDefaultScope = useMemo(
    () =>
      deriveNonOrgDefaultScope({
        activeTeamScope,
        teamsLoading: teamsQuery.isLoading,
        teams: teamsQuery.data ?? [],
        membersLoading: membersQuery.isLoading,
        members: membersQuery.data ?? [],
      }),
    [
      activeTeamScope,
      teamsQuery.isLoading,
      teamsQuery.data,
      membersQuery.isLoading,
      membersQuery.data,
    ],
  );

  const scope = useMemo(
    () =>
      deriveChatScope({
        threadId: activeThreadId,
        threadData: threadQuery.data,
        membersById,
        parsedScope,
        nonOrgDefaultScope,
        restrictToSubtree,
        scopeParam,
        activeTeamScope,
      }),
    [
      activeThreadId,
      threadQuery.data,
      membersById,
      parsedScope,
      nonOrgDefaultScope,
      restrictToSubtree,
      scopeParam,
      activeTeamScope,
    ],
  );

  const topicKey = useMemo(
    () =>
      deriveTopicKey({
        threadData: threadQuery.data,
        topicParam,
        topics,
      }),
    [threadQuery.data, topicParam, topics],
  );

  // Drop a stale/deleted active thread so the rail falls back to a fresh thread.
  useEffect(() => {
    if (!activeThreadId || threadQuery.isLoading) return;
    if (!threadQuery.data) selectThread(null);
  }, [activeThreadId, threadQuery.isLoading, threadQuery.data, selectThread]);

  // ── Context strip ───────────────────────────────────────────────────────────
  // Only the scope is shown here because it is the only context the chat API
  // actually receives; a "current screen" chip would imply the coach knows
  // which page the user is on, which it does not.
  const scopeLabel = useMemo(() => {
    if (scope.kind === "business") return t("scope.organization");
    if (scope.kind === "team") {
      const team = (teamsQuery.data ?? []).find(
        (tm) => tm.id === scope.salesTeamId,
      );
      return team?.name ?? t("scope.team");
    }
    const member = (membersQuery.data ?? []).find(
      (m) => m.id === scope.memberId,
    );
    return member?.name ?? t("scope.member");
  }, [scope, teamsQuery.data, membersQuery.data, t]);

  const railStarters = useRailStarters(businessId, entitled);

  const threadLoading = Boolean(activeThreadId) && threadQuery.isLoading;
  const threadTitle = !activeThreadId
    ? t("threads.new")
    : threadLoading
      ? t("threads.loading")
      : threadQuery.data?.title?.trim() || t("threads.untitled");

  const scopeLocked = Boolean(activeThreadId);
  const ScopeIcon =
    scope.kind === "member" ? User : scope.kind === "team" ? Users : Building2;

  const handleMaximize = () => {
    const sp = new URLSearchParams();
    if (activeThreadId) sp.set("thread", activeThreadId);
    else sp.set("scope", scopeToParam(scope));
    navigate(`/coach?${sp.toString()}`);
  };

  // ── Chrome ──────────────────────────────────────────────────────────────────
  const header = (
    <div className="flex h-12 shrink-0 items-center gap-1 border-b border-border px-3">
      <Popover open={threadPickerOpen} onOpenChange={setThreadPickerOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-1 rounded-lg px-1.5 py-1 text-left transition-colors hover:bg-accent"
          >
            <span
              className={cn(
                "truncate text-[14px] font-semibold",
                threadLoading ? "text-muted-foreground" : "text-foreground",
              )}
            >
              {threadTitle}
            </span>
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={6} className="w-[300px] p-0">
          <CoachThreadPicker
            businessId={businessId}
            activeThreadId={activeThreadId}
            onSelect={selectThread}
            onNewChat={newThread}
          />
        </PopoverContent>
      </Popover>

      <div className="flex shrink-0 items-center gap-0.5">
        <Button
          size="icon"
          variant="ghost"
          className="size-8 text-muted-foreground"
          onClick={newThread}
          aria-label={t("threads.new")}
          title={t("threads.new")}
        >
          <SquarePen className="size-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="size-8 text-muted-foreground"
          onClick={handleMaximize}
          aria-label={t("rail.maximize")}
          title={t("rail.maximize")}
        >
          <Maximize2 className="size-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="size-8 text-muted-foreground"
          onClick={closeRail}
          aria-label={t("rail.close")}
          title={t("rail.close")}
        >
          <PanelRightClose className="size-4" />
        </Button>
      </div>
    </div>
  );

  const contextStrip = (
    <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-border px-3 py-2">
      <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
        {t("rail.context.label")}
      </span>
      {scopeLocked ? (
        <span
          title={t("scope.locked")}
          className="inline-flex items-center gap-1.5 rounded-full border bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground"
        >
          <ScopeIcon className="size-3" aria-hidden="true" />
          <span className="max-w-[160px] truncate">{scopeLabel}</span>
        </span>
      ) : (
        <ScopePicker
          businessId={businessId}
          scope={scope}
          onChange={(next) => setScopeParam(scopeToParam(next))}
          trigger="chip"
          hideOrganizationOption={restrictToSubtree}
        />
      )}
    </div>
  );

  const body = (
    <div className="min-h-0 flex-1">
      <IntelligenceChat
        key={`rail-${resetNonce}`}
        variant="rail"
        businessId={businessId}
        scope={scope}
        onScopeChange={(next) => setScopeParam(scopeToParam(next))}
        topicKey={topicKey}
        onTopicChange={(next) => setTopicParam(next)}
        threadId={activeThreadId}
        onThreadCreated={selectThread}
        onThreadDeleted={() => selectThread(null)}
        onNewChat={newThread}
        topics={topics}
        hideOrganizationScopeOption={restrictToSubtree}
        initialMessage={pendingMessage?.text ?? null}
        onInitialMessageConsumed={clearPendingMessage}
        railStarters={railStarters}
      />
    </div>
  );

  const content = (
    <div className="flex h-full min-h-0 flex-col bg-card">
      {header}
      {contextStrip}
      {body}
    </div>
  );

  if (!entitled || suppressed) return null;

  // On phones the coach is a bottom sheet (M2) — a grab handle over the same
  // content; between the phone and dock breakpoints it's a right overlay sheet.
  if (!canDock) {
    return (
      <Sheet open={open} onOpenChange={(o) => (o ? openRail() : closeRail())}>
        {isMobile ? (
          <SheetContent
            side="bottom"
            className="h-[748px] max-h-[92vh] rounded-t-2xl p-0"
          >
            <SheetTitle className="sr-only">{t("rail.title")}</SheetTitle>
            <div className="flex h-full min-h-0 flex-col">
              <div className="flex shrink-0 justify-center py-2">
                <span className="h-1 w-9 rounded-full bg-border" />
              </div>
              <div className="min-h-0 flex-1">{content}</div>
            </div>
          </SheetContent>
        ) : (
          <SheetContent side="right" className="w-[452px] max-w-[92vw] p-0">
            <SheetTitle className="sr-only">{t("rail.title")}</SheetTitle>
            {content}
          </SheetContent>
        )}
      </Sheet>
    );
  }

  // Docked, the rail is a raised card like the content column beside it (same
  // vertical inset and right gutter as AppLayout's card), not a flat strip on
  // the shell background. Closed, the same card shrinks to a 44px edge so the
  // two states read as one panel folding, not two different surfaces.
  return (
    <aside
      role="complementary"
      aria-label={t("rail.title")}
      className={cn(
        "relative flex h-full shrink-0 flex-col overflow-hidden bg-background py-4 pr-2 transition-[width] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)]",
        open ? "w-[452px]" : "w-[52px]",
      )}
    >
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.12)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_8px_24px_-12px_rgba(0,0,0,0.6)]">
        {open ? (
          content
        ) : (
          <CoachRailEdge
            onOpen={openRail}
            hasThread={Boolean(activeThreadId)}
            openLabel={t("rail.open")}
          />
        )}
      </div>
    </aside>
  );
}

/**
 * The 44px closed edge strip (4b): the open affordance plus one quiet status
 * glyph (a dot when a thread is active). The button carries the accessible
 * name, so the icons are decorative.
 */
function CoachRailEdge({
  onOpen,
  hasThread,
  openLabel,
}: {
  onOpen: () => void;
  hasThread: boolean;
  openLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={openLabel}
      title={openLabel}
      className="flex h-full w-full flex-col items-center gap-4 py-4 text-muted-foreground transition-colors hover:text-foreground"
    >
      <span
        aria-hidden="true"
        className="flex size-7 items-center justify-center rounded-lg bg-primary/10 text-primary"
      >
        <Sparkles className="size-4" />
      </span>
      <span aria-hidden="true" className="relative">
        <MessageSquare className="size-4" />
        {hasThread && (
          <span className="absolute -right-0.5 -top-0.5 size-1.5 rounded-full bg-primary" />
        )}
      </span>
      <span className="mt-auto font-mono text-[10.5px] font-medium [writing-mode:vertical-rl]">
        {shortcutLabel("↵")}
      </span>
    </button>
  );
}
